// 参照専用の埋め込みモード用の認証ハンドオフ。
//
// クロスサイト iframe（Connect Note の記事ページ配下）では、RC の通常認証（OIDC リダイレクト＋
// IndexedDB/localStorage）が使えない（サードパーティ文脈でストレージが分離され、IdP も iframe を
// 拒否する）。そこで親フレーム（Connect Note）が対象 canvasId に束縛された短命の埋め込みチケットを
// postMessage で渡し、RC 埋め込みはそれを API の Bearer に使う。サービス全体スコープの canvas-read
// アクセストークンはブラウザへ渡さない。チケットはメモリ内のみに保持し、ストレージへは
// 一切書かない（分離ストレージ安全・トークン残留防止）。
//
// プロトコル:
//   iframe → 親: { type: "embed-auth-ready" }      … リスナー設置完了を通知（取りこぼし防止のハンドシェイク）
//   親 → iframe: { type: "embed-auth", token, expiresAt? } … 埋め込みチケット受け渡し（token フィールドにチケット）
//   iframe → 親: { type: "embed-auth-refresh" }     … 失効前後の再発行要求
//   親 → iframe: { type: "embed-search", query }    … キャンバス内で強調する検索語（空文字で強調解除）
//   iframe → 親: { type: "embed-search-dismiss" }   … 閲覧者が強調を解除したので、親側の検索指定も解除してほしい
//
// 検索語は認証とは別の関心事だが、受信経路（許可オリジンの検証）を二重に持たないよう同じ
// メッセージリスナーへ相乗りさせる。リスナーの設置・解除は参照数で数え、認証 Shell と参照専用
// ワークスペースのどちらが先に外れても、もう一方の受信が止まらないようにする。
// 親オリジンのロックは認証経路だけが行い、検索語の受信では変更しない。
//
// 検索語の購読はキャンバスの読み込み完了後（参照専用ワークスペースのマウント時）に始まるため、
// 親は ready 直後に検索語を送ると取りこぼす。これを防ぐため直近の検索語を保持し、購読開始時に
// 1 度だけ配る（閲覧者が解除したらその保持も捨てる）。
//
// セキュリティ: 受理は event.origin が getWebConfig().embedParentOrigins に含まれる場合のみ。
// 送信の targetOrigin は必ず具体オリジン（"*" は使わない）。チケット値はログへ出さない。
import { getWebConfig } from "../env";

const PARENT_MESSAGE_TYPE_AUTH = "embed-auth";
const PARENT_MESSAGE_TYPE_SEARCH = "embed-search";
const CHILD_MESSAGE_TYPE_READY = "embed-auth-ready";
const CHILD_MESSAGE_TYPE_REFRESH = "embed-auth-refresh";
const CHILD_MESSAGE_TYPE_SEARCH_DISMISS = "embed-search-dismiss";

/** 期限のこの余裕（ms）を切ったチケットは失効扱いにする。 */
const TOKEN_EXPIRY_GRACE_MS = 30_000;
/** 期限のこの余裕（ms）前に先行して再発行を要求する。 */
const REFRESH_MARGIN_MS = 60_000;

interface EmbedToken {
  readonly token: string;
  /** 親フレームから受け取った expiresAt 由来のエポック ms。取得できなければ undefined（先行 refresh は行わない）。 */
  readonly expiresAtMs: number | undefined;
}

let current: EmbedToken | undefined;
/** 最初に受理した有効メッセージの origin をロックし、以降の送信先に使う。 */
let parentOrigin: string | undefined;
let refreshTimer: number | undefined;
/** message リスナーを必要としている購読者の数。0 → 1 で設置し、1 → 0 で解除する。 */
let messageListenerRefCount = 0;
const subscribers = new Set<() => void>();
/** 親から受け取った検索語の購読者（参照専用ワークスペース）。 */
const searchSubscribers = new Set<(query: string) => void>();
/**
 * 直近に受理した検索語。購読開始が受信より後になっても取りこぼさないために保持する。
 * 未受信は undefined（何も配らない）。閲覧者が解除したら undefined へ戻す。
 */
let lastSearchQuery: string | undefined;

function allowedParentOrigins(): readonly string[] {
  try {
    return getWebConfig().embedParentOrigins;
  } catch {
    return [];
  }
}

function notifySubscribers(): void {
  for (const callback of subscribers) {
    callback();
  }
}

/** 親フレームへメッセージを送る。iframe でない（親＝自分）ときは何もしない。 */
function postToParent(message: { readonly type: string }): void {
  if (typeof window === "undefined" || window.parent === window) {
    return;
  }
  if (parentOrigin !== undefined) {
    window.parent.postMessage(message, parentOrigin);
    return;
  }
  // 親オリジン未確定の間は、許可済みオリジンそれぞれへ送る（targetOrigin が一致する親にだけ届く）。
  for (const origin of allowedParentOrigins()) {
    window.parent.postMessage(message, origin);
  }
}

function scheduleProactiveRefresh(): void {
  if (typeof window === "undefined") {
    return;
  }
  if (refreshTimer !== undefined) {
    window.clearTimeout(refreshTimer);
    refreshTimer = undefined;
  }
  const expiresAtMs = current?.expiresAtMs;
  if (expiresAtMs === undefined) {
    return;
  }
  const delay = Math.max(0, Math.min(expiresAtMs - REFRESH_MARGIN_MS - Date.now(), 2_147_483_647));
  refreshTimer = window.setTimeout(() => {
    requestEmbedTokenRefresh();
  }, delay);
}

function handleMessage(event: MessageEvent): void {
  if (!allowedParentOrigins().includes(event.origin)) {
    return;
  }
  const data: unknown = event.data;
  if (typeof data !== "object" || data === null) {
    return;
  }
  const record = data as Record<string, unknown>;
  if (record["type"] === PARENT_MESSAGE_TYPE_SEARCH) {
    const query = record["query"];
    // query が文字列でないメッセージは無視する（状態を変えない）。
    if (typeof query !== "string") {
      return;
    }
    // ここでは parentOrigin をロックしない。解除通知の宛先は認証経路が確定した親オリジンを使い、
    // 未確定なら postToParent が許可済みオリジンだけへ送る（embed-auth-ready と同じ扱い）。
    lastSearchQuery = query;
    // 反復中の解除（購読解除）で列挙が壊れないよう、控えを取ってから呼ぶ。
    for (const callback of [...searchSubscribers]) {
      callback(query);
    }
    return;
  }
  if (record["type"] !== PARENT_MESSAGE_TYPE_AUTH) {
    return;
  }
  const token = record["token"];
  if (typeof token !== "string" || token.length === 0) {
    return;
  }
  const expiresAtRaw = record["expiresAt"];
  const parsedExpiresAt = typeof expiresAtRaw === "string" ? Date.parse(expiresAtRaw) : Number.NaN;
  // 受理した origin を親としてロックする（以降の refresh 送信先を固定）。
  parentOrigin = event.origin;
  current = {
    token,
    expiresAtMs: Number.isFinite(parsedExpiresAt) ? parsedExpiresAt : undefined,
  };
  scheduleProactiveRefresh();
  notifySubscribers();
}

/**
 * 親フレームからの message リスナーを参照カウント方式で設置する。
 * 返り値を呼ぶと参照が 1 つ減り、誰も必要としなくなった時点で実際に解除する。
 */
function retainMessageListener(): () => void {
  if (typeof window === "undefined") {
    return () => {
      // SSR 等では何もしない。
    };
  }
  if (messageListenerRefCount === 0) {
    window.addEventListener("message", handleMessage);
  }
  messageListenerRefCount += 1;
  let released = false;
  return () => {
    // 同じ解除関数を 2 度呼ばれても参照数が壊れないようにする。
    if (released || typeof window === "undefined") {
      return;
    }
    released = true;
    messageListenerRefCount -= 1;
    if (messageListenerRefCount === 0) {
      window.removeEventListener("message", handleMessage);
    }
  };
}

/**
 * 親フレームからのチケット受け渡しリスナーを設置し、`embed-auth-ready` を通知する。
 * 返り値の関数でリスナーとタイマーを解除する。
 */
export function startEmbedAuthListener(): () => void {
  if (typeof window === "undefined") {
    return () => {
      // SSR 等では何もしない。
    };
  }
  const releaseListener = retainMessageListener();
  // リスナー設置後に ready を通知する（親はこれを待ってチケットを送る＝取りこぼし防止）。
  postToParent({ type: CHILD_MESSAGE_TYPE_READY });
  return () => {
    releaseListener();
    if (typeof window === "undefined") {
      return;
    }
    if (refreshTimer !== undefined) {
      window.clearTimeout(refreshTimer);
      refreshTimer = undefined;
    }
  };
}

/**
 * 親フレームからの検索語（`embed-search`）を購読する。
 *
 * 参照専用の埋め込みワークスペースだけが呼ぶ。編集ビューでは呼ばれないため、検索語の受信経路が
 * そもそも設置されない（編集ビューへ機能が漏れないことの担保の 1 つ）。
 * 購読を始めた時点で直近の検索語があれば、その場で 1 度配る（マウント前に届いた語の取りこぼし防止）。
 * 返り値の関数で購読とリスナーを解除する。
 */
export function startEmbedSearchListener(onQuery: (query: string) => void): () => void {
  const releaseListener = retainMessageListener();
  searchSubscribers.add(onQuery);
  if (lastSearchQuery !== undefined) {
    onQuery(lastSearchQuery);
  }
  return () => {
    searchSubscribers.delete(onQuery);
    releaseListener();
  };
}

/** 閲覧者が強調を解除したことを親フレームへ通知する（親は自身の検索指定を解除する）。 */
export function notifyEmbedSearchDismissed(): void {
  // 解除済みの語を、再マウント時の取りこぼし防止で配り直してしまわないよう忘れる。
  lastSearchQuery = undefined;
  postToParent({ type: CHILD_MESSAGE_TYPE_SEARCH_DISMISS });
}

/** 現在有効な埋め込みチケットを返す。未受領・失効間近は undefined。 */
export function getEmbedAccessToken(): string | undefined {
  if (current === undefined) {
    return undefined;
  }
  if (
    current.expiresAtMs !== undefined &&
    current.expiresAtMs - TOKEN_EXPIRY_GRACE_MS <= Date.now()
  ) {
    return undefined;
  }
  return current.token;
}

/** 親フレームへチケット再発行を要求する。 */
export function requestEmbedTokenRefresh(): void {
  postToParent({ type: CHILD_MESSAGE_TYPE_REFRESH });
}

/** チケット受領を購読する。返り値で解除する。 */
export function subscribeEmbedToken(callback: () => void): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/** 有効チケットを待つ（既にあれば即解決）。timeout 経過時は現在値（undefined の可能性あり）で解決。 */
export function waitForEmbedToken(timeoutMs: number): Promise<string | undefined> {
  const existing = getEmbedAccessToken();
  if (existing !== undefined) {
    return Promise.resolve(existing);
  }
  if (typeof window === "undefined") {
    return Promise.resolve(undefined);
  }
  return new Promise<string | undefined>((resolve) => {
    let settled = false;
    const finish = (value: string | undefined): void => {
      if (settled) {
        return;
      }
      settled = true;
      unsubscribe();
      window.clearTimeout(timer);
      resolve(value);
    };
    const unsubscribe = subscribeEmbedToken(() => {
      const token = getEmbedAccessToken();
      if (token !== undefined) {
        finish(token);
      }
    });
    const timer = window.setTimeout(() => finish(getEmbedAccessToken()), timeoutMs);
  });
}

/**
 * 直前のチケットと「異なる」有効チケットを待つ。401 retry（forceRefresh）で、サーバに拒否された
 * 現行チケットを待機関数が即返して stale なまま再送してしまうのを防ぐ。既に current が previousToken と
 * 異なる有効チケットならそれを返し、timeout 時は undefined を返す（現行チケットにはフォールバックしない）。
 */
export function waitForNextEmbedToken(
  previousToken: string | undefined,
  timeoutMs: number,
): Promise<string | undefined> {
  const now = getEmbedAccessToken();
  if (now !== undefined && now !== previousToken) {
    return Promise.resolve(now);
  }
  if (typeof window === "undefined") {
    return Promise.resolve(undefined);
  }
  return new Promise<string | undefined>((resolve) => {
    let settled = false;
    const finish = (value: string | undefined): void => {
      if (settled) {
        return;
      }
      settled = true;
      unsubscribe();
      window.clearTimeout(timer);
      resolve(value);
    };
    const unsubscribe = subscribeEmbedToken(() => {
      const token = getEmbedAccessToken();
      if (token !== undefined && token !== previousToken) {
        finish(token);
      }
    });
    // timeout 時は現行（拒否された可能性のある）チケットではなく undefined を返し、retry を打ち切らせる。
    const timer = window.setTimeout(() => finish(undefined), timeoutMs);
  });
}

/** テスト用: モジュール内状態を初期化する。 */
export function resetEmbedAuthForTest(): void {
  current = undefined;
  parentOrigin = undefined;
  subscribers.clear();
  searchSubscribers.clear();
  lastSearchQuery = undefined;
  if (typeof window !== "undefined") {
    if (refreshTimer !== undefined) {
      window.clearTimeout(refreshTimer);
      refreshTimer = undefined;
    }
    if (messageListenerRefCount > 0) {
      window.removeEventListener("message", handleMessage);
      messageListenerRefCount = 0;
    }
  }
}
